import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Popconfirm, notification } from 'antd';
import { CopyMinus, FilePlus } from 'lucide-react';
import usePageTitle from '../../../core/hooks/UsePageTitle';
import useMaterias from '../../../core/hooks/UseMaterias';
import DefaultLayout from '../../layouts/default/Default.layout';
import ReviewService, { type BulkCardsRequest } from '../../../sdk/services/ReviewService';
import type { CardLayout, ReviewCardsPage } from '../../../sdk/@types/Revisao';
import { cardTipoMostrado } from '../../components/Revisao/cardTipo';
import CartoesListaEstreita from '../../components/Revisao/CartoesListaEstreita';
import CartoesTable from '../../components/Revisao/CartoesTable';
import { contarDuplicados } from '../../components/Revisao/DuplicadoBadge';
import LoteBar from '../../components/Revisao/LoteBar';
import {
  idsQuePermanecemSelecionados,
  relatoDoLote,
  rotaDeLoteAusente,
  type LoteRelato,
} from '../../components/Revisao/loteSelecao';
import {
  ADMIN_CARDS_PAGE_SIZE,
  resolveAdminList,
  tagOptions,
  type AdminListFilters,
} from './cartoesLista';

type AdminCardRow = ReviewCardsPage['items'][number];

const controlClass =
  'rounded-input border border-edge bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-muted';

const pagerClass =
  'inline-flex items-center justify-center rounded-input border border-edge bg-surface px-3 py-1.5 text-sm text-ink hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50';

function typeButtonClass(selected: boolean): string {
  const base = 'inline-flex items-center gap-2 rounded-chip border px-3 py-1.5 text-sm';
  return selected
    ? `${base} border-select/[0.42] bg-select/[0.14] text-select-ink`
    : `${base} border-edge bg-surface text-ink hover:bg-surface-hover`;
}

function fraseDuplicadosRemovidos(deleted: number): string {
  const truncated = Number.isFinite(deleted) ? Math.trunc(deleted) : 0;
  const n = truncated > 0 ? truncated : 0;
  return n === 1 ? '1 duplicado removido.' : `${n} duplicados removidos.`;
}

export default function GerenciarCartoesView() {
  usePageTitle('Gerenciar cartões');
  const navigate = useNavigate();
  const { materias, fetchMaterias } = useMaterias();

  const [search, setSearch] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [layout, setLayout] = useState<CardLayout | 'all'>('all');
  const [subjectId, setSubjectId] = useState('');
  const [tag, setTag] = useState('');
  const [visiblePage, setVisiblePage] = useState(1);
  const [page, setPage] = useState<ReviewCardsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [duplicadosRemovidos, setDuplicadosRemovidos] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [loteBusy, setLoteBusy] = useState(false);
  const [loteIndisponivel, setLoteIndisponivel] = useState(false);
  const [loteRelato, setLoteRelato] = useState<LoteRelato | null>(null);

  const requestIdRef = useRef(0);
  const appliedFilterKey = useRef<string | null>(null);
  const loteBusyRef = useRef(false);
  const pinnedDuplicateCount = useRef(0);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    fetchMaterias();
  }, [fetchMaterias]);

  const subjectNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const materia of materias ?? []) {
      if (!materia?.id) continue;
      map[String(materia.id)] = String(materia.name ?? '');
    }
    return map;
  }, [materias]);

  const baralhos = useMemo(
    () =>
      (materias ?? []).flatMap((materia) => {
        if (!materia?.id) return [];
        const id = String(materia.id);
        return [{ id, name: materia.name || id }];
      }),
    [materias],
  );

  const load = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    ReviewService.listTemplates({
      q: debouncedQuery,
      layout: layout === 'all' ? undefined : layout,
      subjectId: subjectId || undefined,
      tag: tag || undefined,
      page: visiblePage,
      pageSize: ADMIN_CARDS_PAGE_SIZE,
    })
      .then((next) => {
        if (requestId !== requestIdRef.current) return;
        setPage(next);
        setLoadFailed(false);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setPage(null);
        setLoadFailed(true);
        notification.error({ message: 'Erro ao carregar cartões.' });
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [debouncedQuery, layout, subjectId, tag, visiblePage]);

  const filterKey = `${debouncedQuery}\0${layout}\0${subjectId}\0${tag}`;

  useEffect(() => {
    if (appliedFilterKey.current !== null && appliedFilterKey.current !== filterKey && visiblePage !== 1) {
      appliedFilterKey.current = filterKey;
      setLoading(true);
      setVisiblePage(1);
      return;
    }
    appliedFilterKey.current = filterKey;
    load();
    return () => {
      requestIdRef.current += 1;
    };
  }, [filterKey, visiblePage, load]);

  const dedupe = async () => {
    try {
      setDeduping(true);
      setDuplicadosRemovidos(null);
      const { deleted } = await ReviewService.dedupeTemplates();
      setDuplicadosRemovidos(fraseDuplicadosRemovidos(deleted));
      load();
    } catch {
      notification.error({ message: 'Erro ao remover cartões duplicados.' });
    } finally {
      setDeduping(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await ReviewService.deleteCard(id);
      notification.success({ message: 'Cartão excluído' });
      load();
    } catch {
      notification.error({ message: 'Erro ao excluir o cartão.' });
    }
  };

  const toggleSelected = (cardId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };

  async function runLote(body: BulkCardsRequest): Promise<void> {
    if (loteBusyRef.current || loteBusy || loteIndisponivel || selectedIds.size === 0) return;
    const cardIds = Array.from(selectedIds);
    if (cardIds.length === 0) return;

    const request: BulkCardsRequest =
      body.action === 'moveSubject'
        ? { action: 'moveSubject', cardIds, subjectId: body.subjectId }
        : body.action === 'addTag'
          ? { action: 'addTag', cardIds, tag: body.tag }
          : body.action === 'duplicate'
            ? { action: 'duplicate', cardIds }
            : { action: 'delete', cardIds };

    loteBusyRef.current = true;
    setLoteRelato(null);
    setLoteBusy(true);
    try {
      const result = await ReviewService.bulk(request);
      const relato = relatoDoLote(result);
      setLoteRelato(relato);
      setSelectedIds(new Set(idsQuePermanecemSelecionados(cardIds, result)));
      load();
    } catch (err) {
      if (rotaDeLoteAusente(err)) {
        setLoteIndisponivel(true);
      } else {
        setLoteRelato({
          parcial: true,
          applied: 0,
          skipped: 0,
          message: 'Não foi possível concluir a ação em lote.',
          motivos: [],
        });
      }
    } finally {
      loteBusyRef.current = false;
      setLoteBusy(false);
    }
  }

  const edit = (card: AdminCardRow) => {
    navigate(`/admin/cartoes/${card.id}/editar`, { state: { card } });
  };

  // listTemplates returns a page envelope (serverPaged): items, total, page,
  // pageSize, and typeCounts. A page past the end is that same envelope with
  // items: [] and the real total (HTTP 200) — empty label plus rangeLabel, no
  // error toast. resolveAdminList still windows a bare array in memory.
  const filters: AdminListFilters = { q: search.trim(), layout, subjectId, tag };
  const listWindow = page
    ? resolveAdminList({
        page,
        filters,
        subjectName: (id) => subjectNames[id],
        visiblePage,
        pageSize: ADMIN_CARDS_PAGE_SIZE,
      })
    : null;

  const readyWindow = listWindow && !loadFailed ? listWindow : null;
  const rows: AdminCardRow[] = readyWindow ? readyWindow.rows : [];
  const rowIdsKey = rows.map((row) => row.id).join('\0');

  useEffect(() => {
    const visible = new Set(rowIdsKey === '' ? [] : rowIdsKey.split('\0'));
    setSelectedIds((current) => {
      if (current.size === 0) return current;
      let changed = false;
      const next = new Set<string>();
      current.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : current;
    });
  }, [rowIdsKey]);

  const tagChoices = useMemo(() => {
    const options = tagOptions(page?.items ?? []);
    if (tag !== '' && !options.includes(tag)) return [tag, ...options];
    return options;
  }, [page, tag]);

  const countedDuplicates = page != null && !loadFailed ? contarDuplicados(page.items) : 0;
  if (countedDuplicates > 0) pinnedDuplicateCount.current = countedDuplicates;
  const duplicateCount =
    countedDuplicates > 0 ? countedDuplicates : deduping ? pinnedDuplicateCount.current : 0;

  const typeChoices: { id: CardLayout | 'all'; label: string; count: number | null }[] = [
    { id: 'all', label: 'Todos', count: readyWindow ? readyWindow.announcedTotal : null },
    {
      id: 'basic',
      label: cardTipoMostrado({ layout: 'basic' }),
      count: readyWindow ? readyWindow.typeCounts.basic : null,
    },
    {
      id: 'single',
      label: cardTipoMostrado({ layout: 'single' }),
      count: readyWindow ? readyWindow.typeCounts.single : null,
    },
    {
      id: 'occlusion',
      label: cardTipoMostrado({ layout: 'occlusion' }),
      count: readyWindow ? readyWindow.typeCounts.occlusion : null,
    },
  ];

  const emptyLabel =
    readyWindow && readyWindow.matchTotal > 0 && rows.length === 0
      ? 'Nenhum cartão nesta página'
      : 'Nenhum cartão';

  function filtrosDaLista(): JSX.Element {
    return (
      <>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <select
            aria-label="Matéria"
            value={subjectId}
            onChange={(event) => {
              setSubjectId(event.target.value);
              setVisiblePage(1);
            }}
            className={`w-full ${controlClass}`}
          >
            <option value="">Todas as matérias</option>
            {(materias ?? []).map((materia) => {
              if (!materia?.id) return null;
              const id = String(materia.id);
              return (
                <option key={id} value={id}>
                  {materia.name || id}
                </option>
              );
            })}
          </select>
          <select
            aria-label="Tag"
            value={tag}
            onChange={(event) => {
              setTag(event.target.value);
              setVisiblePage(1);
            }}
            className={`w-full ${controlClass}`}
          >
            <option value="">Todas as tags</option>
            {tagChoices.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>

        {loteRelato ? (
          <div
            role="status"
            aria-live="polite"
            className={`w-full break-words rounded-card border px-3 py-3 text-sm ${
              loteRelato.parcial
                ? 'border-warn/40 bg-warn/[0.08] text-warn-ink'
                : 'border-edge bg-surface text-ink'
            }`}
          >
            <p className={loteRelato.parcial ? undefined : 'text-ok-ink'}>{loteRelato.message}</p>
            {loteRelato.parcial && loteRelato.motivos.length > 0 ? (
              <ul className="mt-2 flex flex-col gap-1">
                {loteRelato.motivos.map((motivo, index) => (
                  <li key={`${index}:${motivo}`} className="break-words">
                    {motivo}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <LoteBar
          count={selectedIds.size}
          baralhos={baralhos}
          busy={loteBusy}
          indisponivel={loteIndisponivel}
          onMove={(destinationSubjectId) => {
            void runLote({
              action: 'moveSubject',
              cardIds: Array.from(selectedIds),
              subjectId: destinationSubjectId,
            });
          }}
          onAddTag={(nextTag) => {
            void runLote({ action: 'addTag', cardIds: Array.from(selectedIds), tag: nextTag });
          }}
          onDuplicate={() => {
            void runLote({ action: 'duplicate', cardIds: Array.from(selectedIds) });
          }}
          onConfirmDelete={() => {
            void runLote({ action: 'delete', cardIds: Array.from(selectedIds) });
          }}
        />
      </>
    );
  }

  return (
    <DefaultLayout hideFilter>
      <div className="flex flex-col gap-4 p-4 sm:gap-6 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-ink">Cartões</h1>
            <p className="mt-1 text-sm text-ink-secondary">
              Todos os flashcards cadastrados. Editar um cartão atualiza a cópia de cada aluno.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {duplicateCount > 0 ? (
              <Popconfirm
                title="Remover cartões duplicados?"
                description="Colapsa cópias repetidas de um mesmo flashcard (uma por aluno) e corrige os contadores de revisão."
                okText="Remover"
                cancelText="Cancelar"
                onConfirm={dedupe}
              >
                <button
                  type="button"
                  disabled={deduping}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-input border border-edge bg-surface px-4 py-2 text-sm font-medium text-ink hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  <CopyMinus size={16} aria-hidden />
                  Remover duplicados ({duplicateCount})
                </button>
              </Popconfirm>
            ) : null}
            <button
              type="button"
              onClick={() => navigate('/admin/cartoes/novo')}
              className="inline-flex w-full items-center justify-center gap-2 rounded-input bg-brand px-4 py-2 text-sm font-semibold text-brand-contrast sm:w-auto max-md:hidden"
            >
              <FilePlus size={16} aria-hidden />
              Novo cartão
            </button>
          </div>
        </div>

        {duplicadosRemovidos ? (
          <div
            role="status"
            aria-live="polite"
            className="break-words rounded-card border border-edge bg-surface px-3 py-3 text-sm text-ink"
          >
            {duplicadosRemovidos}
          </div>
        ) : null}

        <div className="md:hidden">
          <CartoesListaEstreita
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              setVisiblePage(1);
            }}
            types={typeChoices}
            selectedType={layout}
            onSelectType={(id) => {
              setLayout(id);
              setVisiblePage(1);
            }}
            filters={filtrosDaLista()}
            rows={rows}
            subjectName={(id) => subjectNames[id]}
            loading={loading}
            emptyLabel={emptyLabel}
            selecao={{ selectedIds, onToggle: toggleSelected }}
            onEdit={edit}
            onDelete={remove}
            onCreate={() => navigate('/admin/cartoes/novo')}
            rangeLabel={readyWindow ? readyWindow.rangeLabel : null}
            showPager={Boolean(readyWindow && (readyWindow.pageCount >= 2 || readyWindow.page > 1))}
            previousDisabled={!readyWindow || readyWindow.page <= 1}
            nextDisabled={!readyWindow || readyWindow.page >= readyWindow.pageCount}
            onPrevious={() => {
              if (!readyWindow) return;
              setVisiblePage(
                readyWindow.page > readyWindow.pageCount
                  ? readyWindow.pageCount
                  : readyWindow.page - 1,
              );
            }}
            onNext={() => {
              if (readyWindow) setVisiblePage(readyWindow.page + 1);
            }}
          />
        </div>

        <div className="hidden flex-col gap-4 sm:gap-6 md:flex">
          <input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setVisiblePage(1);
            }}
            placeholder="Buscar por conteúdo, matéria, tag ou número"
            aria-label="Buscar por conteúdo, matéria, tag ou número"
            className={`w-full max-w-none sm:max-w-md ${controlClass}`}
          />

          <div className="flex flex-wrap gap-2" role="group" aria-label="Tipo">
            {typeChoices.map((choice) => (
              <button
                key={choice.id}
                type="button"
                aria-pressed={layout === choice.id}
                className={typeButtonClass(layout === choice.id)}
                onClick={() => {
                  setLayout(choice.id);
                  setVisiblePage(1);
                }}
              >
                <span>{choice.label}</span>
                <span className="tabular-nums">{choice.count === null ? '—' : choice.count}</span>
              </button>
            ))}
          </div>

          {filtrosDaLista()}

          <CartoesTable
            rows={rows}
            subjectName={(id) => subjectNames[id]}
            loading={loading}
            emptyLabel={emptyLabel}
            selecao={{ selectedIds, onToggle: toggleSelected }}
            onEdit={edit}
            onDelete={remove}
          />

          {readyWindow ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-ink-secondary" aria-live="polite">
                {readyWindow.rangeLabel}
              </p>
              {readyWindow.pageCount >= 2 || readyWindow.page > 1 ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    className={pagerClass}
                    disabled={readyWindow.page <= 1}
                    onClick={() =>
                      setVisiblePage(
                        readyWindow.page > readyWindow.pageCount
                          ? readyWindow.pageCount
                          : readyWindow.page - 1,
                      )
                    }
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    className={pagerClass}
                    disabled={readyWindow.page >= readyWindow.pageCount}
                    onClick={() => setVisiblePage(readyWindow.page + 1)}
                  >
                    Próxima
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </DefaultLayout>
  );
}
